import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from './useSuiteStore'

interface EnvelopeContactRef {
  uuid: string
  firstname?: string | null
  lastname?: string | null
  email?: string | null
}

export interface EnvelopeSubmitter {
  uuid: string
  externalId: string
  externalReference: string | null
  role: string | null
  contactUuid: string | null
  contact: EnvelopeContactRef | null
  email: string | null
  name: string | null
  phone: string | null
  status: 'AWAITING' | 'SENT' | 'OPENED' | 'COMPLETED' | 'DECLINED'
  sentAt: string | null
  openedAt: string | null
  completedAt: string | null
  declinedAt: string | null
  externalCreatedAt: string | null
  externalUpdatedAt: string | null
  values: unknown
  metadata: unknown
}

interface EnvelopeSalesEngineer {
  uuid: string
  firstName?: string | null
  lastName?: string | null
  email?: string | null
}

interface EnvelopeQuoteRef {
  uuid: string
  quoteReference: string
  totalCa: number | null
  currencyCode: string
  signedAt: string | null
  signedManuallyAt: string | null
  salesEngineer: EnvelopeSalesEngineer | null
}

export interface Envelope {
  uuid: string
  provider: 'DOCUSEAL'
  externalId: string
  templateName: string | null
  entityType: 'QUOTE' | null
  entityUuid: string | null
  companyUuid: string | null
  company: { uuid: string; name: string; referenceExterne: string | null } | null
  status: 'PENDING' | 'COMPLETED' | 'DECLINED' | 'EXPIRED' | 'ARCHIVED'
  expireAt: string | null
  completedAt: string | null
  archivedAt: string | null
  externalCreatedAt: string | null
  externalUpdatedAt: string | null
  metadata: Record<string, unknown> | null
  createdAt: string
  updatedAt: string
  submitters: EnvelopeSubmitter[]
  quote?: EnvelopeQuoteRef | null
}

export interface EnvelopeFilters {
  q?: string
  status?: 'PENDING' | 'COMPLETED' | 'DECLINED' | 'EXPIRED' | 'ARCHIVED' | ''
  entityType?: 'QUOTE' | ''
  companyUuid?: string
  contactUuid?: string
  templateName?: string
}

interface PaginatedEnvelopes {
  data: Envelope[]
  total: number
  limit: number
  offset: number
}

export const useEnvelopeStore = defineStore('envelope', () => {
  const suiteStore = useSuiteStore()

  const envelopes = ref<PaginatedEnvelopes>({ data: [], total: 0, limit: 50, offset: 0 })
  const currentEnvelope = ref<Envelope | null>(null)
  const isLoadingList = ref(false)
  const isLoadingCurrent = ref(false)

  function buildQueryString(filters: EnvelopeFilters, limit: number, offset: number): string {
    const params = new URLSearchParams()
    if (filters.q) params.set('q', filters.q)
    if (filters.status) params.set('status', filters.status)
    if (filters.entityType) params.set('entityType', filters.entityType)
    if (filters.companyUuid) params.set('companyUuid', filters.companyUuid)
    if (filters.contactUuid) params.set('contactUuid', filters.contactUuid)
    if (filters.templateName) params.set('templateName', filters.templateName)
    params.set('limit', String(limit))
    params.set('offset', String(offset))
    return params.toString()
  }

  const fetchEnvelopes = async (
    filters: EnvelopeFilters = {},
    limit = 50,
    offset = 0,
  ): Promise<PaginatedEnvelopes | null> => {
    isLoadingList.value = true
    try {
      const qs = buildQueryString(filters, limit, offset)
      const response = await suiteStore.suiteFetch(`envelope?${qs}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        const data = (await response.json()) as PaginatedEnvelopes
        envelopes.value = {
          data: Array.isArray(data.data) ? data.data : [],
          total: data?.total ?? 0,
          limit: data?.limit ?? limit,
          offset: data?.offset ?? offset,
        }
        return envelopes.value
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des enveloppes:', error)
    } finally {
      isLoadingList.value = false
    }
    return null
  }

  const fetchEnvelopeByUuid = async (uuid: string): Promise<Envelope | null> => {
    isLoadingCurrent.value = true
    try {
      const response = await suiteStore.suiteFetch(`envelope/${uuid}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        const data = (await response.json()) as Envelope
        currentEnvelope.value = data
        return data
      }
    } catch (error) {
      console.error("Erreur lors de la récupération de l'enveloppe:", error)
    } finally {
      isLoadingCurrent.value = false
    }
    return null
  }

  return {
    envelopes,
    currentEnvelope,
    isLoadingList,
    isLoadingCurrent,
    fetchEnvelopes,
    fetchEnvelopeByUuid,
  }
})
